import React, { useState } from 'react';
import {
  Box,
  CssBaseline,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Sidebar from '../Sidebar';
import TopBar from '../TopBar';
import { useBreadcrumbs, useRouteBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useSidebar } from '../../contexts/SidebarContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { sidebarCollapsed, setSidebarCollapsed } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { breadcrumbs } = useBreadcrumbs();
  
  // Automatically update breadcrumbs based on current route
  useRouteBreadcrumbs();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarCollapse = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
  };

  const sidebarWidth = sidebarCollapsed ? 64 : 300;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <CssBaseline />
      <TopBar 
        onMenuClick={handleDrawerToggle}
        sidebarCollapsed={sidebarCollapsed}
        isMobile={isMobile}
        breadcrumbs={breadcrumbs}
      />
      <Box sx={{ display: 'flex', flex: 1 }}>
        <Sidebar
          open={mobileOpen}
          onClose={handleDrawerToggle}
          variant={isMobile ? 'temporary' : 'permanent'}
          onCollapseChange={handleSidebarCollapse}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: { 
              md: isMobile 
                ? '100%' 
                : `calc(100% - ${sidebarWidth}px)` 
            },
            ml: { 
              md: isMobile 
                ? 0 
                : 0 
            },
            mt: '64px', // Account for topbar height
            py: 3, // 24px padding on top and bottom only
            backgroundColor: (theme) => theme.palette.background.default,
            transition: theme.transitions.create(['width', 'margin-left'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
